<template>
  <div align="center">
    <v-sheet class="d-flex flex-column" align="left" color="background">
      <v-window :model-value="currentStep" :touch="false" class="my-5">
        <v-window-item :value="OrderSteps.SITE_SELECTION">
          <v-form>
            <p class="mb-4">
              {{ t("devices.order.siteSelection.title") }}
            </p>
            <v-autocomplete
              v-model="form.stepOne.siteId"
              :items="sites"
              item-title="siteName"
              item-value="siteId"
              :label="t('devices.order.siteSelection.selectSiteLabel')"
              :loading="sitesLoading"
              clearable
              :error-messages="validationErrors(v$.stepOne.$errors)"
              required
            />
          </v-form>
          <v-alert v-if="thereWasAProblem" color="red lighten-2" dark class="mb-4">
            {{ t("devices.order.siteSelection.errorMessage") }}
          </v-alert>
          <div class="text-center">
            <v-btn
              :loading="siteDetailsLoading"
              :disabled="v$.stepOne.$invalid || siteDetailsLoading"
              @click="currentStep = OrderSteps.BASKET_DEVICES"
            >
              {{ t("devices.order.siteSelection.orderDevicesButton") }}
            </v-btn>
          </div>
        </v-window-item>

        <v-window-item :value="OrderSteps.UPDATE_DELIVERY_ADDRESS">
          <div class="d-flex">
            <p class="ms-auto">
              <span class="text-decoration-underline">{{
                t("devices.order.deliveryAddress.orderForSite")
              }}</span>
              {{ form.stepSix.name }}
            </p>
          </div>
          <p class="text-uppercase">
            <span class="font-weight-bold text-deep-purple-darken-4">{{
              t("devices.order.deliveryAddress.title")
            }}</span>
          </p>
          <v-alert variant="outlined" type="warning" class="text-body-2 mb-3">
            {{ t("devices.order.deliveryAddress.warningMessage") }}
          </v-alert>
          <br />
          <v-form>
            <p>{{ t("devices.order.deliveryAddress.description") }}</p>
            <v-text-field
              v-model.trim="form.stepSix.address.line1"
              :label="t('devices.order.deliveryAddress.addressLine1Label')"
              :error-messages="validationErrors(v$.stepSix.address.line1.$errors)"
              required
            />
            <v-text-field
              v-model.trim="form.stepSix.address.line2"
              :label="t('devices.order.deliveryAddress.addressLine2Label')"
            />
            <v-text-field
              v-model.trim="form.stepSix.address.city"
              :label="t('devices.order.deliveryAddress.cityLabel')"
              :error-messages="validationErrors(v$.stepSix.address.city.$errors)"
              required
            />
            <v-text-field
              v-model.trim="form.stepSix.address.postalCode"
              :label="t('devices.order.deliveryAddress.postcodeLabel')"
              :error-messages="validationErrors(v$.stepSix.address.postalCode.$errors)"
              required
            />
            <v-autocomplete
              v-model.trim="form.stepSix.address.country"
              :items="tradedCountries"
              :label="t('devices.order.deliveryAddress.countryLabel')"
              item-title="name"
              item-value="code"
              :error-messages="validationErrors(v$.stepSix.address.country.$errors)"
              :disabled="true"
              required
            />
            <v-text-field
              v-model.trim="form.stepSix.deliveryNotes"
              :label="t('devices.order.deliveryAddress.deliveryNotesLabel')"
            />
          </v-form>
          <div class="d-flex">
            <v-btn
              class="ms-auto"
              :disabled="v$.stepSix.$invalid"
              @click="currentStep = OrderSteps.CONFIRMATION"
            >
              {{ t("form.confirm") }}
            </v-btn>
          </div>
        </v-window-item>

        <v-window-item :value="OrderSteps.BASKET_DEVICES">
          <v-row>
            <v-col cols="12" sm="6">
              <p class="text-uppercase">
                <span class="font-weight-bold text-deep-purple-darken-4">{{
                  t("devices.order.basket.stepTitle")
                }}</span>
                <span class="text-pink">&nbsp;{{ t("devices.order.basket.stepSubtitle") }}</span>
              </p>
            </v-col>
            <v-col cols="12" sm="6">
              <p :class="xs ? 'text-left' : 'text-right'">
                <span class="text-decoration-underline">{{
                  t("devices.order.deliveryAddress.orderForSite")
                }}</span>
                {{ form.stepSix.name }}
              </p>
            </v-col>
          </v-row>
          <device-order-basket v-model:form="form.stepTwo" />
          <div class="d-flex mt-5">
            <v-btn-nd @click="currentStep = OrderSteps.SITE_SELECTION">
              {{ t("form.back") }}
            </v-btn-nd>
            <v-btn
              class="ms-auto"
              :disabled="v$.stepTwo.$invalid"
              @click="currentStep = OrderSteps.POINT_OF_CONTACT"
            >
              {{ t("form.continue") }}
            </v-btn>
          </div>
        </v-window-item>

        <v-window-item :value="OrderSteps.POINT_OF_CONTACT">
          <div class="d-flex">
            <p class="ms-auto">
              <span class="text-decoration-underline">{{
                t("devices.order.deliveryAddress.orderForSite")
              }}</span>
              {{ form.stepSix.name }}
            </p>
          </div>
          <p class="text-uppercase">
            <span class="font-weight-bold text-deep-purple-darken-4">{{
              t("devices.order.pointOfContact.stepTitle")
            }}</span>
            <span class="text-pink">{{ t("devices.order.pointOfContact.stepSubtitle") }}</span>
          </p>
          <br />
          <v-form>
            <p>{{ t("devices.order.pointOfContact.description") }}</p>
            <v-text-field
              v-model.trim="form.stepThree.name"
              :label="t('devices.order.pointOfContact.nameLabel')"
              :error-messages="validationErrors(v$.stepThree.name.$errors)"
              required
            />
            <PhoneNumber v-model="form.stepThree.phoneNumber" />
            <v-text-field
              v-model="form.stepThree.email"
              :label="t('devices.order.pointOfContact.emailLabel')"
              :error-messages="validationErrors(v$.stepThree.email.$errors)"
              required
            />
            <v-text-field
              v-model.trim="form.stepThree.role"
              :label="t('devices.order.pointOfContact.roleLabel')"
              :error-messages="validationErrors(v$.stepThree.role.$errors)"
              required
            />
          </v-form>
          <div class="d-flex">
            <v-btn-nd @click="currentStep = OrderSteps.BASKET_DEVICES">
              {{ t("form.back") }}
            </v-btn-nd>
            <v-btn
              class="ms-auto"
              :disabled="v$.stepThree.$invalid"
              @click="currentStep = OrderSteps.CONFIRMATION"
            >
              {{ t("form.continue") }}
            </v-btn>
          </div>
        </v-window-item>

        <v-window-item :value="OrderSteps.CONFIRMATION">
          <div class="d-flex">
            <p class="ms-auto">
              <span class="text-decoration-underline">{{
                t("devices.order.deliveryAddress.orderForSite")
              }}</span>
              {{ form.stepSix.name }}
            </p>
          </div>
          <p class="text-uppercase">
            <span class="font-weight-bold text-deep-purple-darken-4">{{
              t("devices.order.confirmation.stepTitle")
            }}</span>
            <span class="text-pink">{{ t("devices.order.confirmation.stepSubtitle") }}</span>
          </p>

          <v-form class="pa-5">
            <v-alert variant="outlined" type="warning" class="text-body-2 mb-3">
              {{ t("devices.order.confirmation.warningMessage") }}
            </v-alert>

            <p class="text-uppercase font-weight-medium text-deep-purple-darken-4">
              {{ t("devices.order.confirmation.orderTitle") }}
              <i
                class="material-icons-round"
                style="position: relative; top: 5px; cursor: pointer"
                @click="currentStep = OrderSteps.BASKET_DEVICES"
                >{{ t("views.devices.newDeviceRequestWizard.edit") }}</i
              >
            </p>

            <p v-if="form.stepTwo.s1f2 > 0" class="text-body-2 ms-3">
              {{ form.stepTwo.s1f2 }}
              {{ t("views.devices.newDeviceRequestWizard.deviceWithPrinter") }}
            </p>
            <p v-if="form.stepTwo.s1e2 > 0" class="text-body-2 ms-3">
              {{ form.stepTwo.s1e2 }}
              {{ t("views.devices.newDeviceRequestWizard.deviceWithoutPrinter") }}
            </p>
            <p v-if="form.stepTwo.s1f2Dock > 0" class="text-body-2 ms-3">
              {{ form.stepTwo.s1f2Dock }} {{ t("views.devices.newDeviceRequestWizard.deviceDock") }}
            </p>

            <p class="text-uppercase font-weight-medium text-deep-purple-darken-4">
              {{ t("devices.order.confirmation.deliveryAddressTitle") }}
              <v-btn-nd size="small" variant="tonal" @click="editDeliveryAddress">
                {{ t("views.devices.newDeviceRequestWizard.edit") }}
              </v-btn-nd>
            </p>
            <p class="text-body-2 ms-3">
              {{ form.stepSix.name }}<br />
              {{ form.stepSix.address.line1 }}<br />
              {{ form.stepSix.address.line2 }}<br v-if="form.stepSix.address.line2" />
              {{ form.stepSix.address.postalCode }}<br />
              {{ form.stepSix.address.city }}<br />
              {{ form.stepSix.address.country }}
              <span v-if="form.stepSix.deliveryNotes" class="font-italic"
                ><br />{{ t("devices.order.confirmation.deliveryNotesLabel") }}
                {{ form.stepSix.deliveryNotes }}</span
              >
            </p>

            <p class="text-uppercase font-weight-medium text-deep-purple-darken-4">
              {{ t("devices.order.confirmation.deliveryContactTitle") }}
              <i
                class="material-icons-round"
                style="position: relative; top: 5px; cursor: pointer"
                @click="currentStep = OrderSteps.POINT_OF_CONTACT"
              >
                {{ t("views.devices.newDeviceRequestWizard.edit") }}
              </i>
            </p>

            <p class="text-body-2 ms-3">
              {{ form.stepThree.name }}<br />
              {{ form.stepThree.phoneNumber }}<br />
              {{ form.stepThree.email }}<br />
              {{ form.stepThree.role }}
            </p>

            <v-checkbox
              v-model="form.stepFour.termsAccepted"
              :error-messages="
                v$.stepFour.$invalid ? [t('devices.order.confirmation.termsError')] : []
              "
            >
              <template #label>
                {{ t("devices.order.confirmation.termsCheckbox") }}&nbsp;
                <v-tooltip location="bottom">
                  <template #activator="{ props }">
                    <a
                      href="https://yetipay.me/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-primary text-decoration-none"
                      v-bind="props"
                      @click.stop
                      >{{ t("views.devices.newDeviceRequestWizard.terms") }}</a
                    >
                  </template>
                  {{ t("devices.order.confirmation.termsTooltip") }} </v-tooltip
                >.
              </template>
            </v-checkbox>
          </v-form>
          <div class="d-flex">
            <v-btn
              class="ms-auto"
              :disabled="v$.$invalid"
              :loading="orderLoading"
              @click="placeOrder"
            >
              {{ t("devices.order.confirmation.placeOrderButton") }}
            </v-btn>
          </div>
        </v-window-item>

        <v-window-item :value="OrderSteps.SUCCESS" class="text-center">
          <div v-if="!thereWasAProblem">
            <p class="font-weight-medium">
              {{ t("devices.order.success.title") }}
            </p>
            <p>
              {{ t("devices.order.success.shippingNotification", { name: form.stepThree.name }) }}
            </p>
          </div>
          <div v-else-if="notActiveCustomerOrderError">
            <p class="font-weight-medium">
              {{ t("devices.order.success.errorTitle") }}
            </p>
            <p>{{ t("devices.order.success.deactivatedMessage") }}</p>
          </div>
          <div v-else>
            <p class="font-weight-medium">
              {{ t("devices.order.success.errorTitle") }}
            </p>
            <p>
              {{ t("devices.order.success.genericError") }}<br /><br />
              {{ t("devices.order.success.tryAgainPrompt") }}<br /><br />
              <v-btn
                width="130px"
                height="50px"
                class="text-capitalize"
                color="primary"
                :loading="orderLoading"
                :disabled="orderLoading"
                @click="tryAgain"
              >
                {{ t("devices.order.success.tryAgainButton") }}
              </v-btn>
            </p>
          </div>
        </v-window-item>
      </v-window>
    </v-sheet>
  </div>
</template>

<script setup lang="ts">
import { ListSites200ResponseDataItemsInner } from "@tableyeti/merchant-service/api.ts";
import { ErrorObject, useVuelidate } from "@vuelidate/core";
import { required, email, sameAs, minValue, maxValue } from "@vuelidate/validators";
import { parsePhoneNumber } from "awesome-phonenumber";
import { AxiosError } from "axios";
import * as _ from "lodash-es";
import { inject, computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import { NewDeviceRequest } from "@/api/merchant-service/device";
import { Site } from "@/api/merchant-service/site";
import { useYetipayApi } from "@/composables/useYetipayApi";
import { API } from "@/plugins/api";
import { tradedCountries } from "@/utils/config";

const { t } = useI18n();
const { xs } = useDisplay();
const route = useRoute();

const { api, currentMerchantId } = useYetipayApi();
const legacyApi = inject<API>("api");

enum OrderSteps {
  SITE_SELECTION = 1,
  BASKET_DEVICES = 2,
  POINT_OF_CONTACT = 3,
  CONFIRMATION = 4,
  SUCCESS = 5,
  UPDATE_DELIVERY_ADDRESS = 6,
}

const currentStep = ref(OrderSteps.SITE_SELECTION);
const thereWasAProblem = ref(false);
const thereWasAProblemRetryable = ref(false);
const notActiveCustomerOrderError = ref(false);
const orderLoading = ref(false);
const siteDetailsLoading = ref(false);
const selectedSiteDetails = ref<Site | null>(null);

const form = ref({
  stepOne: {
    siteId: "",
  },
  stepTwo: {
    s1e2: 0,
    s1f2: 0,
    s1f2Dock: 0,
  },
  stepThree: {
    name: "",
    phoneNumber: "",
    email: "",
    role: "",
  },
  stepFour: {
    termsAccepted: false,
  },
  stepSix: {
    name: "",
    address: {
      line1: "",
      line2: "",
      postalCode: "",
      city: "",
      country: "",
    },
    deliveryNotes: "",
  },
});
const v$ = useVuelidate(
  {
    stepOne: {
      siteId: { required, $autoDirty: true },
    },
    stepTwo: {
      s1e2: { minValue: minValue(0), maxValue: maxValue(100), $autoDirty: true },
      s1f2: { minValue: minValue(0), maxValue: maxValue(100), $autoDirty: true },
      s1f2Dock: { minValue: minValue(0), maxValue: maxValue(100), $autoDirty: true },
      valid: () =>
        form.value.stepTwo.s1e2 + form.value.stepTwo.s1f2 + form.value.stepTwo.s1f2Dock > 0,
    },
    stepThree: {
      name: { required, $autoDirty: true },
      phoneNumber: {
        required,
        valid: (value: string) => parsePhoneNumber(value).valid,
        $autoDirty: true,
      },
      email: { required, email, $autoDirty: true },
      role: { required, $autoDirty: true },
    },
    stepFour: {
      termsAccepted: { isChecked: sameAs(true), $autoDirty: true },
    },
    stepSix: {
      address: {
        line1: { required, $autoDirty: true },
        line2: { $autoDirty: true },
        postalCode: { required, $autoDirty: true },
        city: { required, $autoDirty: true },
        country: { required, $autoDirty: true },
      },
      deliveryNotes: { $autoDirty: true },
    },
  },
  form,
);

const sitesLoading = ref(false);
const sites = ref<ListSites200ResponseDataItemsInner[]>([]);

onMounted(async () => {
  if (route.query.siteId) {
    form.value.stepOne.siteId = route.query.siteId as string;
    currentStep.value = OrderSteps.BASKET_DEVICES;
  }
  sitesLoading.value = true;
  try {
    sites.value = await api
      .listSites(currentMerchantId)
      .then((response) => response.data.data.items);
    if (sites.value.length === 1) {
      form.value.stepOne.siteId = sites.value[0].siteId;
    }
  } catch (error) {
    console.error(error);
    thereWasAProblem.value = true;
  } finally {
    sitesLoading.value = false;
  }
});

watch(
  () => form.value.stepOne.siteId,
  async (newSiteId) => {
    if (!newSiteId) return;

    siteDetailsLoading.value = true;
    try {
      const siteDetails: Site = await legacyApi!.site.getSiteDetails(newSiteId);
      selectedSiteDetails.value = siteDetails;
      form.value.stepSix = {
        name: siteDetails.name,
        address: {
          line1: siteDetails.address.line1,
          line2: siteDetails.address.line2 ?? "",
          city: siteDetails.address.city,
          postalCode: siteDetails.address.postalCode,
          country: siteDetails.address.country,
        },
        deliveryNotes: "",
      };
      history.pushState({}, "", route.path); // Remove the query parameter
    } catch (error) {
      console.error(error);
      thereWasAProblem.value = true;
    } finally {
      siteDetailsLoading.value = false;
    }
  },
);
const deliveryAddressUpdated = computed<boolean>(
  () => !_.isEqual(selectedSiteDetails.value?.address, form.value.stepSix.address),
);
const placeOrder = async () => {
  orderLoading.value = true;
  try {
    const request: NewDeviceRequest = {
      siteId: form.value.stepOne.siteId,
      basket: {
        s1e2: form.value.stepTwo.s1e2 > 0 ? form.value.stepTwo.s1e2 : undefined,
        s1f2: form.value.stepTwo.s1f2 > 0 ? form.value.stepTwo.s1f2 : undefined,
        s1f2Dock: form.value.stepTwo.s1f2Dock > 0 ? form.value.stepTwo.s1f2Dock : undefined,
      },
      pointOfContact: form.value.stepThree,
      site: form.value.stepSix,
      deliveryAddressEdited: deliveryAddressUpdated.value,
      termsAccepted: form.value.stepFour.termsAccepted,
    };
    await legacyApi!.device.newDeviceRequest(request);
    thereWasAProblem.value = false;
    thereWasAProblemRetryable.value = false;
  } catch (error) {
    console.error(error);
    thereWasAProblem.value = true;
    if (error instanceof AxiosError) {
      if (error.response?.data.error === "Customer not in status: [active]") {
        notActiveCustomerOrderError.value = true;
      }
      thereWasAProblemRetryable.value =
        !!error.response && error.response.status >= 500 && error.response.status < 600;
    }
  } finally {
    orderLoading.value = false;
  }
  currentStep.value = OrderSteps.SUCCESS;
};
const tryAgain = () => {
  if (thereWasAProblemRetryable.value) {
    placeOrder();
  } else {
    thereWasAProblem.value = false;
    thereWasAProblemRetryable.value = false;
    currentStep.value = OrderSteps.SITE_SELECTION;
  }
};

const editDeliveryAddress = () => {
  currentStep.value = OrderSteps.UPDATE_DELIVERY_ADDRESS;
};

const validationErrors = (errors: ErrorObject[]) => errors.map((e) => e.$message.toString());
</script>
